import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, Grid2, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

type Props = {
	entityParams: { Id: number; Name: string; Entity: string; QueryKey?: string };
	deleteFunction: (partIds: string) => Promise<void>;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const DeleteEntity = ({
	entityParams,
	deleteFunction,
	show,
	onHide,
}: Props) => {
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (show) {
			setIsLoading(false);
		}
	}, [show]);

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteFunction, {
		onSuccess: async () => {
			await queryClient.invalidateQueries([
				`${
					entityParams.QueryKey ? entityParams.QueryKey : entityParams.Entity
				}s`,
			]);
			setMessage(`The ${entityParams.Entity} was deleted successfully`);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsLoading(false),
	});

	const onDelete: (data: number) => Promise<void> = async (data: number) => {
		setIsLoading(true);
		deleteMutation.mutate(`${entityParams.Id}`);
	};

	return (
		<>
			<MesfModal
				title={`DELETE ${entityParams.Entity.toUpperCase()}`}
				open={show}
				handleClose={() => onHide(false)}
				id={`delete-${entityParams.Entity}-modal`}
				maxWidth="md"
			>
				<MesfModal.Content style={{ padding: "15px 30px" }}>
					{isLoading && <CenteredLazyLoading />}
					{!isLoading && (
						<>
							<Grid2 container spacing={1}>
								<Grid2 size={{ md: 12, xs: 12 }}>
									<Typography>
										Are you sure you want to delete{" "}
										<strong>{entityParams?.Name}</strong> {entityParams.Entity}?
									</Typography>
								</Grid2>
							</Grid2>
						</>
					)}
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="secondary"
								onClick={() => onDelete(entityParams.Id)}
								data-test-id="delete-part-button"
							>
								DELETE
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title={`Error Deleting ${entityParams.Entity.charAt(0).toUpperCase() + entityParams.Entity.slice(1)}`}
			/>
		</>
	);
};
